/**
 * Builds the URL to the Pfeiffer Vacuum RMA (service request) site for a given asset.
 *
 * The RMA app lives on a different domain, so the asset is passed as URL query params.
 * The RMA app reads these on load (when `source=installedBase`), clears any stale draft
 * and seeds the product. See vsm-pv-rma LocalStorageProvider.
 *
 * Host is derived from the current hostname, mirroring the env detection in
 * `src/sentry/index.tsx` and `src/keycloak/index.tsx`:
 *   - localhost / testing -> testing-vsm.pfeiffer-vacuum.com
 *   - staging             -> staging-vsm.pfeiffer-vacuum.com
 *   - production          -> vsm.pfeiffer-vacuum.com
 */

function getServiceRequestHost(): string {
    const host = window.location.hostname.toLowerCase();
    if (host.includes("staging")) {
        return "staging-vsm.pfeiffer-vacuum.com";
    }
    if (host.includes("localhost") || host.includes("testing")) {
        return "testing-vsm.pfeiffer-vacuum.com";
    }
    return "vsm.pfeiffer-vacuum.com";
}

interface ServiceRequestParams {
    serialNumber?: string | null;
    productCode?: string | null;
    productName?: string | null;
    /** UI language; languages the RMA site doesn't offer fall back to "en". */
    language?: string;
}

// UI language -> language segment of the RMA site URL (Chinese lives under /cn/).
const RMA_LANGUAGE_SEGMENTS: Record<string, string> = {
    de: "de",
    ko: "ko",
    zh: "cn",
};

export const getServiceRequestLanguage = (language?: string): string =>
    RMA_LANGUAGE_SEGMENTS[(language ?? "").toLowerCase().split("-")[0]] ?? "en";

export function buildServiceRequestUrl({
    serialNumber,
    productCode,
    productName,
    language,
}: ServiceRequestParams): string {
    const lang = getServiceRequestLanguage(language);

    const params = new URLSearchParams();
    if (serialNumber) params.set("serialNo", serialNumber);
    if (productCode) params.set("catalogNo", productCode);
    if (productName) params.set("componentName", productName);
    params.set("source", "installedBase");

    return `https://${getServiceRequestHost()}/${lang}/rma?${params.toString()}`;
}
